import { useMutation } from '@apollo/client';
import { updateExperimentNameMutation } from 'api/mutations/experiments';
import { updateExperimentName } from 'modules/experiments/ducks/actions';
import { addNegativeToast, addPositiveToast, errorIdentifier } from 'modules/toastContainer';
import { useDispatch } from 'react-redux';
import { useExperimentsTranslations } from '../useTranslations';

type CallbackFn = () => void;

type RenameExperiment = {
  id: number;
  showSuccessToaster?: boolean;
  /** Do the same action whether the operation is a success or not */
  onComplete?: CallbackFn;
  onSuccess?: CallbackFn;
  onError?: CallbackFn;
};

const source = 'useRenameExperiment';

export const useRenameExperiment = ({
  id,
  showSuccessToaster = false,
  onComplete,
  onSuccess,
  onError,
}: RenameExperiment) => {
  const { t } = useExperimentsTranslations();
  const dispatch = useDispatch();
  const onSuccessCallback = onSuccess || onComplete;
  const onErrorCallback = onError || onComplete;
  const errorMessage = `Failed to rename experiment id ${id}`;
  const errorToasterMessage = t('errorMessage.updateExperiment');
  const successToasterMessage = t('successMessage.updateExperiment');

  const errorHandler = (error: unknown) => {
    if (onErrorCallback) onErrorCallback();
    dispatch(
      addNegativeToast(
        errorToasterMessage,
        errorIdentifier({
          errorMessage,
          source,
          error,
        }),
      ),
    );
  };

  const [renameExperiment] = useMutation(updateExperimentNameMutation, {
    onError: errorHandler,
  });

  return async (newName: string) => {
    const { data } = await renameExperiment({
      variables: { id, newName },
    });

    if (data?.renameExperiment?.name) {
      if (onSuccessCallback) onSuccessCallback();
      dispatch(updateExperimentName(data.renameExperiment.name));
      if (showSuccessToaster) {
        dispatch(addPositiveToast(successToasterMessage));
      }
    }

    if (data?.renameExperiment?.message) {
      errorHandler(data.renameExperiment.message);
    }
  };
};
